import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building, Search, Pencil, Trash2 } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Modal } from '../components/ui/modal';
import { AdminCompanyDto } from '../models';
import { deleteCompany, getApiError, getCompanies, updateCompany } from '../services/api';

export const CompaniesPage = () => {
  const [items, setItems] = useState<AdminCompanyDto[]>();
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState<AdminCompanyDto>();
  const [formName, setFormName] = useState('');
  const [formResponsible, setFormResponsible] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState<AdminCompanyDto>();
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setError('');
    getCompanies().then((r) => setItems(r.data.data)).catch((e) => setError(getApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (company: AdminCompanyDto) => {
    setEditing(company);
    setFormName(company.name);
    setFormResponsible(company.responsibleFio || '');
    setFormPhone(company.phone || '');
  };

  const handleUpdate = async () => {
    if (!editing || !formName.trim()) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await updateCompany(editing.id, {
        name: formName.trim(),
        responsibleFio: formResponsible.trim() || undefined,
        phone: formPhone.trim() || undefined,
      });
      setSuccess(`Клиент "${formName.trim()}" обновлён`);
      setEditing(undefined);
      load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await deleteCompany(deleteCandidate.id);
      setSuccess(`Клиент "${deleteCandidate.name}" удалён`);
      setDeleteCandidate(undefined);
      load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = useMemo(
    () => (items || []).filter((c) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return c.name.toLowerCase().includes(q)
        || c.responsibleFio?.toLowerCase().includes(q)
        || String(c.id).includes(q);
    }),
    [items, query],
  );

  return (
    <AppLayout title="Компании">
      <PageHeader
        title="Клиенты организаций"
        description="Клиенты создаются администраторами в личных кабинетах; здесь доступно сервисное управление."
      />
      {error && <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs text-foreground">{items?.length ?? 0} компаний</span>
          </div>
          <div className="ml-auto flex w-full max-w-xs items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Найти компанию"
              className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {!items && !error ? <LoadingState /> : items && filtered.length === 0 ? (
          <EmptyState icon={<Building className="h-8 w-8" />} title={query ? 'Компании не найдены' : 'Нет компаний'} description={query ? 'Измените поисковый запрос.' : 'Компании появятся после регистрации.'} />
        ) : items ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Название</th>
                  <th className="px-4 py-3 font-medium">Ответственный</th>
                  <th className="px-4 py-3 font-medium">Телефон</th>
                  <th className="px-4 py-3 text-center font-medium">Tenant ID</th>
                  <th className="px-4 py-3 text-right font-medium">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">#{c.id}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-xs font-semibold text-primary">
                          {c.name.slice(0, 2).toUpperCase()}
                        </div>
                        <p className="font-medium text-foreground">{c.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">{c.responsibleFio || '—'}</td>
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{c.phone || '—'}</td>
                    <td className="px-4 py-4 text-center font-mono text-xs text-muted-foreground">{c.tenantId}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(c)} title="Редактировать" className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button disabled={c.id === 1} onClick={() => setDeleteCandidate(c)} title={c.id === 1 ? 'Системного клиента удалить нельзя' : 'Удалить'} className="flex h-8 w-8 items-center justify-center rounded-md border border-destructive/20 bg-destructive/5 text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-30">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <Modal open={Boolean(editing)} onClose={() => setEditing(undefined)} title={`Редактирование клиента #${editing?.id ?? ''}`} maxWidth="max-w-md"
        footer={<><Button variant="ghost" onClick={() => setEditing(undefined)} disabled={submitting}>Отмена</Button><Button onClick={handleUpdate} disabled={submitting || !formName.trim()}>{submitting ? 'Сохранение...' : 'Сохранить'}</Button></>}
      >
        <div className="space-y-4">
          <Input label="Название *" value={formName} maxLength={50} onChange={(e) => setFormName(e.target.value)} />
          <Input label="Ответственный (ФИО)" value={formResponsible} maxLength={200} onChange={(e) => setFormResponsible(e.target.value)} />
          <Input label="Телефон" value={formPhone} maxLength={20} onChange={(e) => setFormPhone(e.target.value)} />
          <p className="text-xs text-muted-foreground">Tenant #{editing?.tenantId}. Перенос клиента между организациями запрещён.</p>
        </div>
      </Modal>

      <Modal open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(undefined)} title="Удаление клиента" maxWidth="max-w-md"
        footer={<><Button variant="ghost" onClick={() => setDeleteCandidate(undefined)} disabled={submitting}>Отмена</Button><Button variant="destructive" onClick={handleDelete} disabled={submitting}>{submitting ? 'Удаление...' : 'Удалить'}</Button></>}
      >
        <p className="text-sm text-muted-foreground">Клиент «{deleteCandidate?.name}» исчезнет из списков. История завершённых осмотров сохранится.</p>
      </Modal>
    </AppLayout>
  );
};
